<template>
  <div>
    <h1>Pre-existing will upload</h1>
    <h3>Upload your pre-existing will</h3>
    <p>Only PDF documents allowed</p>
    <v-file-input
      @click="sendGADataLayer"
      v-model="file"
      single-line
      outlined
      dense
      filled
      :loading="buttonLoading"
      accept="application/pdf"
      :label="fileInputText"
      class="file-upload-btn"
    />

    <WillUploadProgress :loading="buttonLoading" />

    <SetWillLocation :testator="testator" :people-relations="peopleRelations" />

    <div class="text-center mt-5">
      <v-btn rounded depressed large color="grey" @click="$emit('hideForm')">
        Close
      </v-btn>
    </div>

    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />

    <v-dialog v-model="identityMismatchDialog" max-width="560" persistent>
      <WillIdentityMismatchDialog
        :message="identityMismatchMessage"
        :prompt="identityMismatchPrompt"
        :loading="identityMismatchConfirming"
        @confirm="confirmIdentityMismatch"
        @cancel="cancelIdentityMismatch"
      />
    </v-dialog>
  </div>
</template>

<script>
  import SetWillLocation from '../will_verification/SetWillLocation.vue';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import { formHelperMixin } from '../../mixins/form-helper-mixin';
  import { willIdentityMismatchMixin } from '../../mixins/will-identity-mismatch-mixin';
  import WillIdentityMismatchDialog from '../common/WillIdentityMismatchDialog.vue';
  import WillUploadProgress from '../common/WillUploadProgress.vue';
  import { CREATE_PRE_EXISTING_WILL_MUTATION } from '../../constants/graphql/will-verifications';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';

  export default {
    name: 'PreExistingWillForm',
    components: {
      SetWillLocation,
      ErrorSnackbar,
      SuccessSnackbar,
      WillIdentityMismatchDialog,
      WillUploadProgress,
    },
    mixins: [formHelperMixin, willIdentityMismatchMixin],
    props: {
      testator: {
        type: Object,
        required: true,
      },
      peopleRelations: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        file: null,
        showSuccess: false,
        successMessage: null,
        errorMessage: null,
      };
    },
    computed: {
      preExistingWillExists() {
        return this.testator.preExistingWills.length;
      },
      fileInputText() {
        return this.preExistingWillExists ? 'Re-upload will' : 'Upload will';
      },
    },
    watch: {
      file(newValue) {
        if (newValue) {
          this.submit();
        }
      },
    },
    methods: {
      sendGADataLayer() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'pre-existing-will-upload',
          omuserid: this.$store.getters?.userId,
        });
      },
      submit() {
        this.buttonLoading = 'success';
        var reader = new FileReader();
        reader.readAsDataURL(this.file);
        reader.onload = function (evt) {
          const fileBase64 = evt.target.result;
          this.createPreExistingWill(fileBase64);
        }.bind(this);
      },
      createPreExistingWill(fileBase64) {
        this.performUpload(fileBase64, false);
      },
      performUpload(fileBase64, acknowledgedMismatch) {
        const testatorId = this.testatorId;

        this.$apollo
          .mutate({
            mutation: CREATE_PRE_EXISTING_WILL_MUTATION,
            variables: {
              testatorId,
              fileBase64,
              acknowledgedMismatch,
            },
            update: (cache, { data: { createPreExistingWill } }) => {
              this.updateCacheAfterCreatePreExistingWill(
                cache,
                createPreExistingWill
              );
            },
          })
          .then(_response => {
            this.buttonLoading = false;
            this.file = null;
            this.showSuccessMessage();
          })
          .catch(error => {
            if (this.handleIdentityMismatch(error, fileBase64)) return;

            if (error.message.includes('GraphQL error: Invalid file type')) {
              this.errorMessage = 'Invalid file type. Please upload a pdf.';
            } else {
              this.reportUnexpectedUploadError(error);
              return;
            }
            this.displayError();
            this.file = null;
          });
      },
      updateCacheAfterCreatePreExistingWill(cache, createPreExistingWill) {
        const query = {
          query: TESTATOR_QUERY,
          variables: { id: this.testatorId },
        };
        const data = cache.readQuery(query);

        data.testator.preExistingWills.push(createPreExistingWill);

        cache.writeQuery({ ...query, data });
      },
      showSuccessMessage() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'will-successfully-uploaded',
          omuserid: this.$store.getters?.userId,
        });
        this.successMessage = 'Will successfully uploaded';
        this.showSuccess = true;
      },
    },
  };
</script>
